<template>
  <div class="comunicado-detalle" v-if="model">
    <h3>{{ model.Asunto }}</h3>
    <div class="datos-comunicado card">
      <div class="row">
        <div class="form-group col-md-4">
          <label class="dx-field-item-label-text">{{ $t("comunicados.form.tipo_via_comunicacion") }}</label>
          <div class="mt-1">{{ textoTipo }}</div>
        </div>
        <div class="form-group col-md-4 fecha-envio" v-if="model.FechaEnvio">
          <label class="dx-field-item-label-text">{{ $t("comunicados.grid.fecha_envio") }}</label>
          <div class="mt-1">{{ fechaEnvioTexto }}</div>
        </div>
        <div class="form-group col-md-4" v-if="model.FechaEnvio">
          <label class="dx-field-item-label-text">{{ $t("comunicados.detalle.destinatarios") }}</label>
          <div class="mt-1">
            <DxSelectBox
              v-model:value="destinatarioSeleccionadoId"
              :data-source="destinatarios"
              v-bind="selectDestinatariosOptions"
            />
          </div>
        </div>
      </div>
      <div class="row mt-2">
        <div class="form-group col-md-4">
          <label class="dx-field-item-label-text">{{ $t("comunicados.form.tipo_empresa_agente_dirigido") }}</label>
          <div class="mt-1">
            <div v-if="model.esValorTodos(model.TipoEmpresaAgenteDirigido)" class="d-inline-block rounded py-1 px-2 tipoEmpresaAgente TODAS">
              {{ $t("comunicados.grid.todos") }}
            </div>
            <div v-else :class="['d-inline-block rounded py-1 px-2', `tipoEmpresaAgente ${model.TipoEmpresaAgenteDirigido!.replace(' ', '')}`]">
              {{ $t(`empresa_agente.tipo.${model.TipoEmpresaAgenteDirigido}`) }}
            </div>
          </div>
        </div>
        <div class="form-group col-md-4">
          <label class="dx-field-item-label-text">{{ $t("comunicados.form.roles") }}</label>
          <div class="mt-1 roles">
            <div v-if="!model.RolesDirigidos.length" class="d-inline-block rounded py-1 px-2 rol TODOS">
              {{ $t("comunicados.grid.todos") }}
            </div>
            <div v-for="(rol, index) in model.RolesDirigidos" :key="`${rol}-${index}`" class="d-inline-block rounded py-1 px-2 me-1" :class="`rol ${rol}`">
              {{ $t(`usuarios.rol.${rol}`) }}
            </div>
          </div>
        </div>
        <div v-if="esPortal && destinatarioSeleccionado" class="form-group col-md-4">
          <label class="dx-field-item-label-text">{{ $t("comunicados.detalle.fecha_lectura") }}</label>
          <div v-if="destinatarioSeleccionado.Leido && destinatarioSeleccionado.FechaLeido" class="mt-1">{{ fechaLecturaTexto }}</div>
          <div v-else class="mt-1">{{ $t("comunicados.detalle.no_leido") }}</div>
        </div>
      </div>
      <div class="row mt-2">
        <div class="form-group col-md-12">
          <label class="dx-field-item-label-text">{{ $t("comunicados.form.empresas_agentes") }}</label>
          <div class="mt-1 empresas-agentes">
            <span v-if="!model.EmpresasAgentesDirigidos.length" class="empresa-agente todas">
              {{ $t("comunicados.grid.todas") }}
            </span>
            <template v-else v-for="(empresa, index) in model.EmpresasAgentesDirigidos" :key="`${empresa}-${index}`">
              <span v-if="index !== 0">, </span>
              <span class="empresa-agente">{{ empresa }}</span>
            </template>
          </div>
        </div>
      </div>
    </div>
    <div class="mensaje-comunicado card">
      <div class="row g-2">
        <div class="form-group col-md-12">
          <div class="mt-1 comunicado-detalle__cuerpo" v-html="model.Cuerpo"></div>
        </div>
      </div>
      <div class="row mt-2 border-top pt-3">
        <div class="form-group col-md-12">
          <label class="dx-field-item-label-text">{{ $t("tickets.form.adjuntos") }}</label>
          <AdjuntosList :adjuntos="model.rutasAdjuntos" :texto-no-adjuntos="$t('comunicados.detalle.sin_adjuntos')" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, onMounted, ref } from "vue";
import DxSelectBox from "devextreme-vue/select-box";
import { useModalBuilder } from "@helpers/modal-building-helper";
import { closeButtonProps } from "@helpers/button-helper";
import { getSelectBoxDefaultProperties } from "@helpers/grid-helper";
import { messageBoxResult } from "@components/dialogs/message-box";
import { useApi } from "@api/integration";
import { useIntl } from "@services/i18n";
import { toastError } from "@services/notification-service";
import { type AgenteComunicadoListModel, TipoComunicadoEnum } from "@api/api";
import { ComunicadoDetailModel } from "@/components/comunicados/models/ComunicadoDetailModel";
import AdjuntosList from "@/.shared/components/AdjuntosList.vue";

const intl = useIntl();
const api = useApi();

const props = defineProps({
  model: {
    type: Object as () => ComunicadoDetailModel,
    required: true,
  },
});

const destinatarios = ref<AgenteComunicadoListModel[]>([]);
const destinatarioSeleccionadoId = ref<string | null>(null);

const selectDestinatariosOptions = {
  ...getSelectBoxDefaultProperties(),
  placeholder: intl.i18n.t("comunicados.detalle.destinatarios_placeholder"),
};

const destinatarioSeleccionado = computed(() =>
  destinatarios.value.find((destinatario) => destinatario.Id === destinatarioSeleccionadoId.value) ?? null,
);

const esPortal = computed(() => props.model.Tipo === TipoComunicadoEnum.Portal);

const textoTipo = computed(() => intl.i18n.t(`comunicado.tipo.${props.model.Tipo}`));

const formatearFecha = (fecha: Date) => new Intl.DateTimeFormat(intl.i18n.locale.value, {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false,
}).format(new Date(fecha));

const fechaEnvioTexto = computed(() => {
  if (!props.model?.FechaEnvio) {
    return "";
  }

  return formatearFecha(props.model.FechaEnvio);
});

const fechaLecturaTexto = computed(() => {
  if (!destinatarioSeleccionado.value?.FechaLeido) {
    return "";
  }

  return formatearFecha(destinatarioSeleccionado.value.FechaLeido);
});

const cargarDestinatarios = async () => {
  if (!props.model.FechaEnvio) {
    return;
  }

  try {
    destinatarios.value = await api.callStandardApi(
      "getListadoAgentesComunicado",
      { showLoading: true },
      props.model.Id,
    );
  } catch {
    toastError(intl.i18n.t("comunicados.detalle.error_cargar_destinatarios"));
  }
};

onMounted(async () => {
  const modal = useModalBuilder();
  modal.setContext(getCurrentInstance()!);

  modal
    .addButton("close", closeButtonProps(), { toolbar: "bottom", location: "after" })
    .update((w) => (w.onClick = () => modal.close(messageBoxResult.cancel)));

  await cargarDestinatarios();
});
</script>

<style lang="scss" scoped>
.comunicado-detalle {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.datos-comunicado,
.mensaje-comunicado {
  padding: var(--spacing-md);
}

.mensaje-comunicado {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}
</style>
